export default class LoginDropdown {
    private readonly loginTrigger: HTMLAnchorElement | null;
    private readonly sidebarLoginTrigger: HTMLAnchorElement | null;
    private readonly loginBox: HTMLElement | null;
    private readonly accountContainer: HTMLElement | null;
    private readonly forgotPasswordLink: HTMLAnchorElement | null;
    private readonly backToLoginLink: HTMLAnchorElement | null;
    private readonly rememberForm: HTMLElement | null;
    private readonly loginUsernameInput: HTMLInputElement | null;
    private readonly rememberUsernameInput: HTMLInputElement | null;

    private onMenuOpenCallback: (() => void) | null = null;
    private onLoginFormShownCallback: (() => void) | null = null;

    constructor() {
        this.loginTrigger = document.querySelector('.h_account.login .h_login_link');
        this.sidebarLoginTrigger = document.querySelector('.sidebar_login a');
        this.loginBox = document.querySelector('.login_box');
        this.accountContainer = document.querySelector('.h_account.login');
        this.forgotPasswordLink = document.querySelector('.login_box .forgot_password');
        this.backToLoginLink = document.querySelector('.remember_form .back_to_login');
        this.rememberForm = document.querySelector('.remember_form');

        this.loginUsernameInput = document.querySelector<HTMLInputElement>('#loginUsername1');
        this.rememberUsernameInput = document.querySelector<HTMLInputElement>('#forgotUsername1');

        this.bindClickEvents();
        this.bindEscapeKey();
        this.bindFocusOutHandler();
    }

    public onMenuOpen(callback: () => void): void {
        this.onMenuOpenCallback = callback;
    }

    public onLoginFormShown(callback: () => void): void {
        this.onLoginFormShownCallback = callback;
    }

    public closeMenu(): void {
        this.closeLoginForm();
        this.closeRememberForm();
        this.accountContainer?.classList.remove('active');
    }

    private bindClickEvents(): void {
        this.loginTrigger?.addEventListener('click', this.handleLoginTriggerClick);
        this.sidebarLoginTrigger?.addEventListener('click', this.handleLoginTriggerClick);
        this.forgotPasswordLink?.addEventListener('click', this.handleForgotPasswordClick);
        this.backToLoginLink?.addEventListener('click', this.handleBackToLoginClick);
    }

    private bindEscapeKey(): void {
        document.addEventListener('keydown', this.handleEscapeKey);
    }

    private bindFocusOutHandler(): void {
        document.addEventListener('focusin', this.handleFocusOutside);
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    private handleFocusOutside = (event: FocusEvent): void => {
        const target = event.target as HTMLElement;
        this.evaluateOutsideInteraction(target);
    };

    private handleClickOutside = (event: MouseEvent): void => {
        const target = event.target as HTMLElement;
        this.evaluateOutsideInteraction(target);
    };

    private evaluateOutsideInteraction(target: HTMLElement): void {
        const isInAccountContainer = this.accountContainer?.contains(target) ?? false;

        if (!isInAccountContainer) {
            this.closeMenu();
        }
    }

    private handleLoginTriggerClick = (event: Event): void => {
        event.preventDefault();
        if (this.isLoginFormOpen()) {
            this.closeMenu();
        } else {
            this.openMenuWithCredentialFallback();
        }
    };

    private handleForgotPasswordClick = (event: Event): void => {
        event.preventDefault();
        this.showRememberForm();
    };

    private handleBackToLoginClick = (event: Event): void => {
        event.preventDefault();
        this.showLoginForm();
    };

    private handleEscapeKey = (event: KeyboardEvent): void => {
        if (event.key !== 'Escape') return;

        if (this.isRememberFormOpen()) {
            this.showLoginForm();
        } else if (this.isLoginFormOpen()) {
            this.closeMenu();
        }
    };

    private isLoginFormOpen(): boolean {
        return this.loginBox?.classList.contains('open') ?? false;
    }

    private isRememberFormOpen(): boolean {
        return this.rememberForm?.classList.contains('open') ?? false;
    }

    private openMenu(): void {
        this.showLoginForm();
        this.accountContainer?.classList.add('active');
        this.onMenuOpenCallback?.();
    }

    private openMenuWithCredentialFallback(): void {
        if (!('PasswordCredential' in window)) {
            this.openMenu();
            return;
        }

        navigator.credentials.get({password: true} as any).then((credential: any) => {
            if (!credential) {
                this.openMenu();
                return;
            }

            const loginForm = document.querySelector<HTMLFormElement>('#loginFormMain');
            if (!loginForm) {
                this.openMenu();
                return;
            }

            const csrfToken = loginForm.querySelector<HTMLInputElement>('input[name="clx_csrf_token"]')?.value || '';
            const action = loginForm.getAttribute('action') || '';
            const returnUrl = window.location.href;

            // @ts-ignore
            loginService.login(credential.id, credential.password, returnUrl, csrfToken, action).done((data: any) => {
                if (data.success === true) {
                    window.location.reload();
                    return;
                }

                this.openMenu();
                this.displayLoginError(loginForm, data.message);
            });
        });
    }

    private showLoginForm(): void {
        this.loginBox?.classList.add('open');
        this.loginBox?.classList.remove('closed');

        this.rememberForm?.classList.remove('open');
        this.rememberForm?.classList.add('closed');

        this.resetErrors('#loginFormMain');
        this.onLoginFormShownCallback?.();

        if (this.loginUsernameInput) {
            setTimeout(() => {
                this.loginUsernameInput?.focus();
            }, 100);
        }
    }

    private showRememberForm(): void {
        this.loginBox?.classList.remove('open');
        this.loginBox?.classList.add('closed');

        this.rememberForm?.classList.add('open');
        this.rememberForm?.classList.remove('closed');

        this.resetErrors('.forgot_password_form');

        if (this.rememberUsernameInput) {
            setTimeout(() => {
                this.rememberUsernameInput?.focus();
            }, 100);
        }
    }

    private closeLoginForm(): void {
        this.loginBox?.classList.remove('open');
        this.loginBox?.classList.add('closed');
    }

    private closeRememberForm(): void {
        this.rememberForm?.classList.remove('open');
        this.rememberForm?.classList.add('closed');
    }

    private displayLoginError(form: HTMLFormElement, message: string): void {
        const container = form.closest('.login');
        const messageBox = container?.querySelector('.show_login_message') as HTMLElement;
        const loginField = container?.querySelector('.login_field') as HTMLElement;

        if (messageBox) {
            messageBox.classList.add('error_msg', 'active');
            messageBox.innerHTML = `<strong>${message}</strong>`;
        }

        if (loginField) {
            loginField.classList.add('field_with_errors');
        }
    }

    private resetErrors(scopeSelector: string): void {
        const scope = document.querySelector(scopeSelector);
        if (!(scope instanceof HTMLElement)) return;

        scope.querySelectorAll('.show_login_message').forEach((el) => {
            el.classList.remove('active');
            el.innerHTML = '';
        });

        scope.querySelectorAll('.login_field.field_with_errors').forEach((el) => {
            el.classList.remove('field_with_errors');
        });
    }
}
